// @atlas/contracts — a `assets` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { z } from 'zod';
import { nullableStr } from '../_shared/schemas.js';
/**
 * Eszköz-sor (workforce leltár + igénylés-workflow, belső nézet): a `workforce.assets` [T] busz-erőforrás
 * fehérlistás mezői (assetNumber/name/category/type/status/netPrice/…) + a kapcsolt entitások BFF-oldali
 * feloldása: birtokló (`holderIdentityId` → `holderName`), igénylő (`requesterIdentityId` → `requesterName`),
 * csapat (`teamId` → `teamName` a workforce.teams i18n JSON-nevéből; NULL → „iroda"). Az eszköz a CSAPATHOZ
 * tartozik (a hierarchia-bevezetéskor a részleg-szintről mozdult ide); a részlege a csapatból következik. A
 * `netPrice` az Odinból Decimal→string formában érkezik — a szerződés STRING-ként hordozza (a pontosság
 * megőrzéséért; a HUF-formázás a UI dolga). A `legacyId` sosem jön. A `holderId`/`requesterId`/`teamId`
 * a nyers UUID (a UI a *-Name mezőt jeleníti). Az esemény-napló (asset_events) MÉG NINCS betöltve — v1 lista.
 */
export const AssetRowSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    assetNumber: z.string().nullable(),
    name: z.string(),
    /** hardware | software. */
    category: z.string(),
    /** desktop_pc | laptop | monitor | … | operating_system | antivirus | … (a legacy AssetTypeEnum 1:1). */
    type: z.string(),
    /** requested | rejected | approved | being_procured | purchased | received | spare | in_use | retired. */
    status: z.string(),
    serialNumber: z.string().nullable(),
    licenseKey: z.string().nullable(),
    /** Nettó ár — Decimal→string (a HUF-formázás a UI dolga). */
    netPrice: z.string().nullable(),
    vatRate: z.string().nullable(),
    /** office | home | social | mixed | null. */
    usageLocation: z.string().nullable(),
    purchaseDate: z.string().nullable(),
    /**
     * A csapat — SZÁMOLT (Ilcsa #114): személyhez rendelt eszköznél a birtokos (igénynél a „kinek", önmagának
     * igénylésnél az igénylő) AKTUÁLIS beosztásának csapata; irodai eszköznél `null`. Nem tárolt adat, így egy
     * csapat- vagy részlegváltás után az eszköz magától az új csapatnál jelenik meg.
     */
    teamId: z.string().nullable(),
    /** A csapat neve (a `teamId`-hez); irodai eszköznél `null`. */
    teamName: z.string().nullable(),
    /** A csapat szín-tokenje (a teamId-hez) — a csapat-oszlop színes badge-éhez. */
    teamColor: z.string().nullable(),
    /** A csapat RÉSZLEGÉNEK neve (két szint feloldva) — a szűrőhöz és a kontextushoz; NULL teamId → null. */
    departmentName: z.string().nullable(),
    holderId: z.string().nullable(),
    holderName: z.string().nullable(),
    /** A birtokló név-részei + avatar + csapat-szín (locale-tudatos névsorhoz + színes profilkép, mint a többi lista). */
    holderGivenName: z.string().nullable(),
    holderFamilyName: z.string().nullable(),
    holderAvatarUrl: z.string().nullable(),
    holderTeamColor: z.string().nullable(),
    requesterId: z.string().nullable(),
    requesterName: z.string().nullable(),
    /** Az igénylő név-részei + avatar + csapat-szín (ha nincs birtokló, az igénylő jelenik meg). */
    requesterGivenName: z.string().nullable(),
    requesterFamilyName: z.string().nullable(),
    requesterAvatarUrl: z.string().nullable(),
    requesterTeamColor: z.string().nullable(),
    createdAt: z.string().nullable(),
    /** Szerkesztő-form előtöltéshez (a lista nem jeleníti): beszerzés helye / számla-szám / selejtezés dátuma + OCC. */
    purchaseLocation: z.string().nullable(),
    invoiceNumber: z.string().nullable(),
    /**
     * A gyártó/termék weboldala (a legacy `assets.website`, `nullable|url`). A SOROn kell lennie, mert a
     * szerkesztő-form ebből töltődik elő — enélkül minden mentés kinullázta (mérve: 37 hardver-eszköznek van
     * weboldala).
     */
    website: z.string().nullable(),
    retirementDate: z.string().nullable(),
    /** Az igénylő megjegyzése az igényléshez (indoklás, konfiguráció, határidő) — csak olvasható a sorban. */
    requestNote: z.string().nullable(),
    /**
     * KINEK igényelték (kedvezményezett) — `null` = az igénylő magának. Az átvételkor ő lesz a birtokos. A név a
     * BFF-ben oldódik fel (mint a birtokló / igénylő neve).
     */
    requestedForId: z.string().nullable(),
    requestedForName: z.string().nullable(),
    /** A kedvezményezett név-részei — a locale-tudatos névsorhoz (mint a birtokló / igénylő mezőinél). */
    requestedForGivenName: z.string().nullable(),
    requestedForFamilyName: z.string().nullable(),
    /**
     * Lejárat / megújítás napja (`YYYY-MM-DD`) — előfizetéses szoftverhez, licenchez; hardvernél mindig `null`.
     * Erre megy a napi lejárat-értesítő (30 és 7 nappal előtte + a lejárat napján).
     */
    expiryDate: z.string().nullable(),
    /**
     * IRODAI (általános, közös) használat — nincs birtokos-személy és nincs csapat, a felhasználás helye fixen
     * `social`. Igénynél: irodának igényelték (nincs „kinek").
     */
    officeUse: z.boolean().default(false),
    version: z.number(),
});
/** A személyhez rendelt eszköz felhasználási helyei (az irodaié fixen `social`). */
export const PERSONAL_USAGE_LOCATIONS = ['office', 'home', 'mixed'];
export const OFFICE_USAGE_LOCATION = 'social';
/**
 * Az iroda / személy szabály (Ilcsa #114, #122) — a leltár- és az igény-body KÖZÖS ellenőrzése:
 *   • irodai (`officeUse`): nincs birtokos / „kinek", a hely `social` (vagy hiányzik — a BFF állítja be);
 *   • személyes: a hely csak `office` | `home` | `mixed` (vagy üres).
 */
function refineOfficeRule(v, ctx, personField) {
    const loc = v.usageLocation ?? null;
    if (v.officeUse === true) {
        if (v.person)
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [personField],
                params: { reason: 'office_no_person' },
            });
        if (loc !== null && loc !== OFFICE_USAGE_LOCATION)
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['usageLocation'],
                params: { reason: 'office_social_only' },
            });
    }
    else if (loc !== null && !PERSONAL_USAGE_LOCATIONS.includes(loc)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['usageLocation'],
            params: { reason: 'personal_location' },
        });
    }
}
/**
 * Pénz-/százalék-mező a wire-en: NEM-ÜRES decimal-string (`12000` vagy `12000.50`) — a Decimal pontossága
 * miatt sosem `number`. KÖTELEZŐ (nem nullable): az Odin `workforce.assets` sorában a `net_price`/`vat_rate`
 * NOT NULL (a legacy 1:1), ezért egy `null` a buszon üres mező-listás 422-t adott — a hiba a felületen
 * „Az eszköz mentése nem sikerült." volt, mező-jelzés nélkül (élőben mérve). A mező-szintű
 * üzenetet a web errorMap-je adja (`validation:assets.<mező>.required|format`).
 */
const decimalString = (example) => z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,2})?$/, `Decimal-string (pl. ${example})`);
/**
 * Eszköz create/update közös body (INVENTORY CRUD — a több-lépcsős igény-workflow KÜLÖN, 2. increment). A
 * `workforce/assets` [T] INTERNAL busz-sor whitelistelt, szerkeszthető mezői. A `category`-t a BFF a `type`-ból
 * származtatja (a legacy 1:1); a `status` a KÉZZEL választható halmaz (purchased/in_use/spare/retired) — a
 * workflow-státuszok (requested/approved/…) a 2. increment. `retirementDate` a selejtezéshez (status=retired).
 * A `netPrice`/`vatRate` Decimal→string. Az `assetNumber` create-kor GENERÁLT (MOR-prefix + per-kategória sorszám).
 */
export const AssetWriteBodySchema = z
    .object({
    name: z.string().trim().min(1).max(255),
    type: z.string().trim().min(1).max(64),
    status: z.enum(['purchased', 'in_use', 'spare', 'retired']),
    serialNumber: nullableStr(255),
    licenseKey: nullableStr(255),
    /** Nettó ár — KÖTELEZŐ decimal-string (az Odin-oszlop NOT NULL). */
    netPrice: decimalString('12000 vagy 12000.50'),
    /** Áfa-kulcs — KÖTELEZŐ decimal-string (az Odin-oszlop NOT NULL). */
    vatRate: decimalString('27'),
    purchaseDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')
        .nullable()
        .optional(),
    retirementDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')
        .nullable()
        .optional(),
    /**
     * Lejárat / megújítás napja (előfizetés, licenc). CSAK szoftvernél értelmezett: hardver-típusnál a BFF `null`-ra
     * állítja (mint a selejtezés dátumát nem-kivezetett státusznál).
     */
    expiryDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')
        .nullable()
        .optional(),
    purchaseLocation: nullableStr(255),
    invoiceNumber: nullableStr(255),
    /**
     * KINÉL van: egy munkatárs (`holderIdentityId`) VAGY az iroda (`officeUse: true`, általános használat) — a kettő
     * közül pontosan egy. A CSAPAT nem bemenet (Ilcsa #114): a birtokos beosztásából számolódik, irodánál nincs.
     */
    holderIdentityId: z.string().uuid().nullable().optional(),
    officeUse: z.boolean().optional(),
    usageLocation: nullableStr(32),
})
    .superRefine((v, ctx) => {
    refineOfficeRule({ ...v, person: v.holderIdentityId }, ctx, 'holderIdentityId');
    if (v.officeUse !== true && !v.holderIdentityId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['holderIdentityId'],
            params: { reason: 'holder_required' },
        });
    }
});
/**
 * Eszköz-szerkesztő opció-listák: CSAPATOK (kis lista → select), a részlegük nevével csoportosításhoz.
 * A birtokló EntityCombobox-szal (identity-keresés).
 */
export const AssetRefsSchema = z.object({
    teams: z.array(z.object({ id: z.string(), name: z.string(), departmentName: z.string().nullable() })),
});
// ── Eszköz-KATALÓGUS (Ilcsa #113) — szerkeszthető kategóriák és típusok ─────────────────────────────────────
/** Kétnyelvű név (a magyar kötelező) — a kiadás-kategóriák mintájára. */
export const AssetCatalogNameSchema = z.object({
    hu: z.string().trim().min(1).max(120),
    en: z.string().trim().max(120).nullable().optional(),
});
export const AssetTypeRowSchema = z.object({
    id: z.string(),
    /** STABIL kulcs — az eszköz `type` mezője ezt hordozza (a név szabadon átírható). */
    key: z.string(),
    categoryId: z.string(),
    name: AssetCatalogNameSchema,
    position: z.number(),
    /** Hány eszköz / igény (bármely státuszban) hivatkozik rá — ha > 0, a típus nem törölhető. */
    usageCount: z.number(),
    version: z.number(),
});
export const AssetCategoryRowSchema = z.object({
    id: z.string(),
    /** STABIL kulcs — az eszköz `category` mezője ezt hordozza. */
    key: z.string(),
    name: AssetCatalogNameSchema,
    /** A leltári szám előtagja (pl. `MORH`) — 2–8 nagybetű / szám. */
    numberPrefix: z.string(),
    /** Szoftver-jellegű: licenckulcs és lejárat (lejárat-figyelés) tartozik hozzá. */
    isSoftware: z.boolean(),
    position: z.number(),
    version: z.number(),
    types: z.array(AssetTypeRowSchema),
});
export const AssetCatalogSchema = z.object({ categories: z.array(AssetCategoryRowSchema) });
export const AssetCategoryWriteBodySchema = z.object({
    name: AssetCatalogNameSchema,
    numberPrefix: z
        .string()
        .trim()
        .regex(/^[A-Z0-9]{2,8}$/),
    isSoftware: z.boolean(),
});
export const AssetTypeWriteBodySchema = z.object({
    name: AssetCatalogNameSchema,
    categoryId: z.string().uuid(),
});
/** Eszköz-lista lekérdezés: státusz- + típus-szűrő (a legacy enumok 1:1) + szabad-szöveg (név/leltári szám/
 *  sorozatszám/birtokló) + szerver-rendezés (sort/order) + offset-lapozás (a BFF-szűrt halmazon). A `sort` a
 *  rendezés-kulcs (assetNumber | name | type | status | holderName | teamName | netPrice | purchaseDate) —
 *  a BFF whitelisttel validál (ismeretlen → assetNumber, a legacy default; irány default DESC). */
export const AssetListQuerySchema = z.object({
    q: z.string().optional(),
    /**
     * Melyik nézet (tab): `inventory` = a nálunk lévő eszközök (received/spare/in_use/retired),
     * `request` = az igénylés-/beszerzés-folyamat (requested/approved/rejected/being_procured/purchased).
     * A legacy hardver/szoftver tab az inventory, az igények-tab a request. Default: inventory.
     */
    scope: z.enum(['inventory', 'request']).optional(),
    status: z.string().optional(),
    /** hardware | software — a kategória-facet (a típus-opciókat is ez szűkíti a UI-ban). */
    category: z.string().optional(),
    type: z.string().optional(),
    /** Felhasználás helye (office | home | social | mixed) — a leltár-fül facet-szűrője. */
    usageLocation: z.string().optional(),
    /**
     * CSAPAT-szűrő — a csapat UUID-ja (nem a neve): a név csak megjelenítés, és két csapat átnevezés után
     * ütközhetne. Üres/hiányzó → nincs szűrés. Mindkét fülön (leltár és igénylés) értelmezett.
     *
     * MIÉRT KELL (mért eset): egy csapat törlését az eszköz-referencia guardja megakadályozza (409), a
     * felületen viszont NEM lehetett megtalálni, MELYIK eszköz tartozik hozzá — a leltár-fül a csapatot nem is
     * mutatta. A guard így egy megválaszolhatatlan kérdést tett fel.
     */
    team: z.string().optional(),
    /**
     * MUNKATÁRS-szűrő (Ilcsa #112) — identity UUID: illeszkedik, ha ő a birtokos, vagy ő a „kinek" (önmagának
     * igénylésnél az igénylő). A szabad-szöveges keresőtől független.
     */
    holder: z.string().optional(),
    sort: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
/**
 * Eszköz-IGÉNYLÉS létrehozás body (workflow, B lépcső). A `category`-t a BFF a `type`-ból származtatja
 * (legacy 1:1); a `netPrice`/`vatRate` KÖTELEZŐ Decimal-string (a busz-oszlop NOT NULL); a `website`
 * igényléskori termék-link (üres string → null). A `status=requested` + a `requesterIdentityId`
 * (aktuális identity a JWT-ből) a BFF-en áll be — a kliens NEM küldi. A `note` az igénylő megjegyzése, a
 * `requestedForIdentityId` a kedvezményezett (KINEK igényli; üres / a saját azonosítója → magának).
 */
const assetRequestFields = {
    type: z.string().trim().min(1).max(64),
    name: z.string().trim().min(1).max(255),
    netPrice: decimalString('12000 vagy 12000.50'),
    vatRate: decimalString('27'),
    // SECURITY: csak http(s) — a z.string().url() a javascript: sémát is átengedné (tárolt tüske).
    // Nincs explicit üzenet: a lokalizált errorMap (validation:assets.website.*) adja a mező-hibát.
    website: z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? null : v), z
        .string()
        .trim()
        .max(2083)
        .regex(/^https?:\/\/\S+$/)
        .nullable()
        .optional()),
    note: nullableStr(2000),
    /** KINEK: munkatárs (üres / a saját azonosító → magának) — VAGY az iroda (`officeUse`). A csapat ebből számolódik. */
    requestedForIdentityId: z.string().uuid().nullable().optional(),
    /** Irodának (általános, közös használatra) igényelve — ilyenkor nincs „kinek" és nincs csapat (#114). */
    officeUse: z.boolean().optional(),
    /** Felhasználás helye (#122) — irodainál fixen `social`, személyesnél office | home | mixed; beérkezéskor átkerül. */
    usageLocation: nullableStr(32),
};
export const AssetRequestCreateBodySchema = z
    .object(assetRequestFields)
    .superRefine((v, ctx) => refineOfficeRule({ ...v, person: v.requestedForIdentityId }, ctx, 'requestedForIdentityId'));
/**
 * Igény SZERKESZTÉSE (Ilcsa #116) — ugyanazok a mezők, mint a felvételnél. Ki szerkesztheti: az igénylő a SAJÁT
 * igényét a döntésig; a bíráló (döntési jog) a beérkezésig. Bírálói módosításról az igénylő értesítést kap.
 */
export const AssetRequestUpdateBodySchema = z
    .object(assetRequestFields)
    .superRefine((v, ctx) => refineOfficeRule({ ...v, person: v.requestedForIdentityId }, ctx, 'requestedForIdentityId'));
/** Igénylés-döntés body: approve|reject. A `rejectReason` CSAK reject-nél értelmezett — approve mellett 422
 *  (mező-szintű, BFF-szabály); reject-nél opcionális (a note az asset-events sorba is bekerül). */
export const AssetDecideBodySchema = z.object({
    decision: z.enum(['approve', 'reject']),
    rejectReason: nullableStr(1000),
});
/** Beszerzés-előrehaladás body: a BFF állapotgépe SZIGORÚ sorrendet érvényesít
 *  (approved→being_procured→purchased→received) — sorrend-sértés → 409. */
export const AssetProgressBodySchema = z.object({
    step: z.enum(['being_procured', 'purchased', 'received']),
});
//# sourceMappingURL=schemas.js.map